/*
Contains all Supabase queries for easy reference and reusability.
*/

import { message } from "antd";
import dayjs from "dayjs";

import { timeSince } from "../utils/general";
import supabase, { getSupaClient } from "./supabase";

const { Svix } = require("svix");

const svix = new Svix(
  process.env.NODE_ENV === "development"
    ? process.env.REACT_APP_SVIX_AUTH_TOKEN_TEST
    : process.env.REACT_APP_SVIX_AUTH_TOKEN
);

const dateFormat = "MM/DD/YY";

/**
 * Loads the available timezones for a given team
 * @param {*} teamId - the team id
 * @returns - a list of timezones for a given team
 */
const getTeamTimezones = async (teamId) => {
  const supaClient = getSupaClient();
  const { data, error } = await supaClient.rpc("team_timezones", {
    team_id: teamId,
  });

  if (error) {
    console.log("Error in getTeamTimezones: ", error);
  }

  return data || [];
};
export { getTeamTimezones };

/**
 * Get total Slack users gropued by day, week or month for a given time period
 * @param {*} teamdId the Slack team id
 * @param {*} startTime start time
 * @param {*} endTime end time
 * @param {*} timePeriod the period of time to group the users by: "day", "week", "month"
 * @returns An array of total users grouped by day, week or month
 */
const getTotalUsersByTimePeriod = async (
  teamId,
  startTime,
  endTime,
  timePeriod
) => {
  const supaClient = getSupaClient();
  const workspaceId =
    JSON.parse(localStorage.getItem("supabase.auth.token") || "{}")
      ?.currentSession.user?.app_metadata.workspace || "";

  if (workspaceId) {
    const params = {
      team_id: teamId,
      start_time: startTime,
      end_time: endTime,
      workspace: workspaceId,
      interval_type: timePeriod,
    };
    // eslint-disable-next-line
    let response = await supaClient.rpc("slack_total_users_time", params);
    if (response.error) {
      console.log("Error in getTotalUsersTimePeriod:", response.error);
      return [];
    }
    // Parse the response data
    response.data = response.data.map((item) => {
      if (timePeriod === "day") {
        return { day: item.date_period, total: item.total };
      }
      if (timePeriod === "week") {
        return { week: item.date_period, total: item.total };
      }
      if (timePeriod === "month") {
        return { month: item.date_period, total: item.total };
      }
      return [];
    });
    return response.data || [];
  }
  return [];
};
export { getTotalUsersByTimePeriod };

/**
 * Get new Slack users gropued by day, week or month for a given time period
 * @param {*} teamdId the Slack team id
 * @param {*} startTime start time
 * @param {*} endTime end time
 * @param {*} timePeriod the period of time to group the users by: "day", "week", "month"
 * @returns An array of new users grouped by day, week or month
 */
const getNewUsersByTimePeriod = async (
  teamId,
  startTime,
  endTime,
  timePeriod
) => {
  const supaClient = getSupaClient();
  const workspaceId =
    JSON.parse(localStorage.getItem("supabase.auth.token") || "{}")
      ?.currentSession.user?.app_metadata.workspace || "";

  if (workspaceId) {
    const params = {
      team_id: teamId,
      start_time: startTime,
      end_time: endTime,
      workspace: workspaceId,
      period_type: timePeriod,
    };
    // eslint-disable-next-line
    let response = await supaClient.rpc("slack_new_users_time", params);
    if (response.error) {
      console.log("Error in getNewUsersTimePeriod:", response.error);
      return [];
    }
    // Parse the data
    response.data = response.data.map((item) => {
      if (timePeriod === "day") {
        return { day: item.date_period, total: item.total };
      }
      if (timePeriod === "week") {
        return { week: item.date_period, total: item.total };
      }
      if (timePeriod === "month") {
        return { month: item.date_period, total: item.total };
      }
      return [];
    });
    return response.data || [];
  }
  return [];
};
export { getNewUsersByTimePeriod };

/**
 * Get the given team's workspace id
 * @param {string} teamId
 * @returns {null or int} null if a workspace wasn't found, or id if found
 */
const getWorkspaceId = async (teamId) => {
  const supaClient = getSupaClient();
  const { data, error } = await supaClient
    .from("teams")
    .select("workspace")
    .match({ slack_id: teamId });
  if (error) {
    console.log("error in getWorkspaceId", error);
    return null;
  }
  return data[0].workspace;
};
export { getWorkspaceId };

/*
Purpose: Get channels for a given team
Params:
  teamId: team slack_id
*/
async function getChannels(teamId) {
  const supaClient = getSupaClient();
  const { data, error } = await supaClient
    .from("channels")
    .select()
    .filter("is_deleted", "eq", false)
    .filter("is_archived", "eq", false)
    .filter("team", "eq", teamId);
  if (error) console.log("error in getChannels", error);
  return data;
}
export { getChannels };

/*
Purpose: Get discord channels for a given team
Params:
  guildId: guild id
*/
async function getDiscordChannels(guildId) {
  const supaClient = getSupaClient();
  const { data, error } = await supaClient
    .from("discord_channels")
    .select()
    .filter("guild", "eq", guildId);
  if (error) console.log("error in getDiscordChannels", error);
  return data;
}
export { getDiscordChannels };

/*
Purpose: Get all users sorted by activity counts for a given time period and team.
Params:
  startTime: start of time period
  endTime: end of time period
  teamId: team slack_id
*/
async function getAllUsers(startTime, endTime, teamId) {
  const supaClient = getSupaClient();
  const params = {
    start_time: startTime,
    end_time: endTime,
    team_id: teamId,
  };

  const { data, error } = await supaClient.rpc("all_users", params);
  if (error) console.error("error in getAllUsers", error);
  return data;
}
export { getAllUsers };

/*
Purpose: Get all users sorted by activity counts for a given time period and team.
Params:
  startTime: start of time period
  endTime: end of time period
  teamId: team slack_id
*/
async function getAllUsersPaginated(teamId) {
  const supaClient = getSupaClient();
  let allUsers = [];
  let done = false;
  const pageSize = 10000;
  let from = 0;
  /* eslint-disable no-await-in-loop */
  while (!done) {
    const { data, error } = await supaClient
      .from("slack_users")
      .select(
        `
          slack_id, 
          key:slack_id, 
          name,
          user_name:name,
          image_512, 
          image_32, 
          email, 
          tags(id, name, color), 
          is_invited,
          directory_location, 
          directory_experience`
      )
      .eq("team", teamId)
      .neq("is_bot", true)
      .neq("is_deleted", true)
      .range(from, from + pageSize);
    if (data) {
      allUsers = allUsers.concat(data);
      if (data.length < 10000) {
        done = true;
      } else {
        from += pageSize;
      }
    }
    if (error) {
      console.log("error in getAllUsersPaginated", error);
    }
  }
  /* eslint-enable no-await-in-loop */
  return allUsers;
}
export { getAllUsersPaginated };

async function getUsersPaginated(teamId) {
  const supaClient = getSupaClient();
  let allUsers = [];
  let done = false;
  const pageSize = 10000;
  let from = 0;
  /* eslint-disable no-await-in-loop */
  while (!done) {
    const { data, error } = await supaClient
      .from("slack_users")
      .select(`slack_id, name, image_32`)
      .eq("team", teamId)
      .neq("is_bot", true)
      .neq("is_deleted", true)
      .range(from, from + pageSize);

    if (data) {
      allUsers = allUsers.concat(data);
      if (data.length < 10000) {
        done = true;
      } else {
        from += pageSize;
      }
    }
    if (error) {
      console.log("error in getUsersPaginated", error);
    }
  }
  /* eslint-enable no-await-in-loop */
  return allUsers;
}
export { getUsersPaginated };

/*
Purpose: Get initial 10k users sorted by activity counts
Params:
  teamId: team slack_id
*/
async function getInitialUsers(teamId) {
  const supaClient = getSupaClient();
  let initialUsers = [];
  const pageSize = 10000;
  let cursor = 0;
  /* eslint-disable no-await-in-loop */
  const { data, error } = await supaClient
    .from("slack_users")
    .select(
      `
        slack_id, 
        key:slack_id, 
        name,
        user_name:name,
        image_512, 
        image_32, 
        email, 
        tags(id, name, color),
        is_invited,  
        directory_location,
        joined_formatted_ts,
        tz_label,
        directory_experience`
    )
    .eq("team", teamId)
    .neq("is_bot", true)
    .neq("is_deleted", true)
    .range(0, pageSize)
    .order("joined_formatted_ts", {
      ascending: false,
      nullsFirst: true,
    })
    .order("slack_id", {
      ascending: false,
      nullsFirst: true,
    });
  if (data) {
    initialUsers = initialUsers.concat(data);
    if (data.length >= pageSize) {
      cursor = pageSize;
    }
  }
  if (error) {
    console.log("error in getAllUsersPaginated", error);
  }
  /* eslint-enable no-await-in-loop */
  return { initialUsers, cursor };
}
export { getInitialUsers };

/*
Purpose: Get all users from a cursor to the remaining.
Params:
  teamId: team slack_id
*/
async function getAllUsersPaginatedFrom(teamId, cursor) {
  const supaClient = getSupaClient();
  let allUsers = [];
  let done = false;
  const pageSize = 10000;
  let from = cursor;
  /* eslint-disable no-await-in-loop */
  while (!done) {
    const { data, error } = await supaClient
      .from("slack_users")
      .select(
        `
        slack_id,
        key:slack_id,
        name,
        user_name:name,
        image_512,
        image_32,
        email,
        tags(id, name, color),
        is_invited,
        directory_location,
        joined_formatted_ts,
        tz_label,
        directory_experience`
      )
      .eq("team", teamId)
      .neq("is_bot", true)
      .neq("is_deleted", true)
      .range(from, from + pageSize)
      .order("joined_formatted_ts", {
        ascending: false,
        nullsFirst: true,
      })
      .order("slack_id", {
        ascending: false,
        nullsFirst: true,
      });
    if (data) {
      allUsers = allUsers.concat(data);
      if (data.length < pageSize) {
        done = true;
      } else {
        from += pageSize;
      }
    }
    if (error) {
      console.log("error in getAllUsersPaginated", error);
    }
  }
  /* eslint-enable no-await-in-loop */
  return allUsers;
}
export { getAllUsersPaginatedFrom };

/*
Purpose: Get new users sorted by date joined for a given time period and team.
Params:
  startTime: start of time period
  endTime: end of time period
  teamId: team slack_id
  count: (optional) if true, return only the count of new users
*/
async function getNewUsers(startTime, endTime, teamId, count) {
  const supaClient = getSupaClient();
  const params = {
    start_time: startTime,
    end_time: endTime,
    team_id: teamId,
  };
  const { data, error } = await supaClient.rpc("new_users", params);
  if (error) console.error("error in getNewUsers", error);
  if (count) {
    return data.length;
  }
  return data;
}
export { getNewUsers };

/*
 * Purpose: Get the most active users for a given team and time period.
 * Params:
    startTime: start of time period
    endTime: end of time period
    teamId: team slack_id
 */
async function getActiveUsers(startTime, endTime, teamId) {
  const supaClient = getSupaClient();
  const params = {
    start_time: startTime,
    end_time: endTime,
    team_id: teamId,
  };

  const { data, error } = await supaClient.rpc("active_users", params);
  if (error) console.error("error in getActiveUsers", error);
  return data;
}
export { getActiveUsers };

/*
 * Purpose: Get the inactive Slack users for a given period of time
 * Params:
 * startTime: start of time period
 * endTime: end of time period
 * teamId: team id
 */
async function getInactiveUsers(startTime, endTime, teamId) {
  const supaClient = getSupaClient();
  const params = {
    start_time: startTime,
    end_time: endTime,
    team_id: teamId,
  };

  const { data, error } = await supaClient.rpc(
    "slack_inactive_members_timeperiod",
    params
  );
  if (error) console.error("error in getInactiveSlackUsers", error);
  return data;
}
export { getInactiveUsers };

/*
 * Purpose: Get the at-risk users for a given team.
 * Params:
    teamId: team slack_id
    count: (optional) if true, return only the count of new users
 */
async function getAtRiskUsers(teamId, riskData, count) {
  const supaClient = getSupaClient();
  const params = {
    team_id: teamId,
    active_period_start: riskData ? riskData?.active_period_start : 90,
    inactive_period_start: riskData ? riskData.inactive_period_start : 30,
  };
  const { data, error } = await supaClient.rpc("custom_at_risk_users", params);
  if (error) console.error("error in getAtRiskUsers", error);
  if (count) {
    return data.length;
  }
  return data;
}
export { getAtRiskUsers };

/*
 * Purpose: Get the at-risk users for a given team. Calls the custom_at_risk_users_dashboard RPC
 * which only returns slack_ids of at-risk users for a more efficient query
 * Params:
    teamId: team slack_id
    count: (optional) if true, return only the count of new users
 */
async function getAtRiskUsersDashboard(teamId, riskData) {
  const supaClient = getSupaClient();
  const params = {
    team_id: teamId,
    active_period_start: riskData ? riskData?.active_period_start : 90,
    inactive_period_start: riskData ? riskData.inactive_period_start : 30,
  };
  const { data, error } = await supaClient.rpc(
    "custom_at_risk_users_dashboard",
    params
  );
  if (error) console.error("error in getAtRiskUsersDashboard", error);
  return {
    data,
  };
}
export { getAtRiskUsersDashboard };

/*
 * Purpose: Get the at-risk users for a given team.
 * Params:
    teamId: team slack_id
    count: (optional) if true, return only the count of new users
 */
async function getAtRiskUsersTwoWeeks(teamId, count) {
  const supaClient = getSupaClient();
  const params = {
    team_id: teamId,
  };
  const { data, error } = await supaClient.rpc(
    "at_risk_users_two_weeks",
    params
  );
  if (error) console.error("error in getAtRiskUsersTwoWeeks", error);
  if (count) {
    return data.length;
  }
  return data;
}
export { getAtRiskUsersTwoWeeks };

/* Load a given user's directory profile information
 */
const loadDirectoryProfile = async (userId) => {
  const supaClient = getSupaClient();
  const { data, error } = await supaClient
    .from("directory_users")
    .select(
      `slack_id,
      name,
      image_512,
      image_32,
      directory_skills (id, name),
      directory_experience,
      directory_looking,
      directory_connecting,
      directory_pronouns,
      directory_twitter,
      directory_linkedin,
      directory_interest_free_text,
      directory_industry_experience (id, name),
      directory_bio,
      directory_visible,
      directory_title,
      directory_location,
      directory_edited`
    )
    .match({ slack_id: userId });
  if (error) {
    console.log("error in loadDirectoryProfile", error);
  }
  if (data) {
    // console.log("directory ", data[0].directory_skills.length);
    return data[0];
  }
  return null;
};
export { loadDirectoryProfile };

/*
Purpose: Get channel counts for a given member over a given time period.
Params:
  startTime: start of time period
  endTime: end of time period
  teamId: member's team slack_id
  userId: member id
*/
const memberChannelCounts = async (startTime, endTime, teamId, userId) => {
  const supaClient = getSupaClient();
  const params = {
    start_time: startTime,
    end_time: endTime,
    team_id: teamId,
    user_id: userId,
  };

  const { data, error } = await supaClient.rpc("user_channel_counts", params);
  if (error) console.error("error in memberDetailChannelCounts", error);
  return data;
};

export { memberChannelCounts };

/*
Purpose: Get recent activity for a member over a given time period.
Params:
  userId: member id
  startTime: start of time period
  endTime: end of time period
*/
const memberRecentActivity = async (startTime, endTime, userId) => {
  const supaClient = getSupaClient();
  const params = {
    start_time: startTime,
    end_time: endTime,
    user_id: userId,
  };

  const { data, error } = await supaClient.rpc("recent_user_activity", params);
  if (error) console.error("error in memberDetailRecentActivity", error);
  return data;
};
export { memberRecentActivity };

/** 
* Purpose: Load popular posts for a given team and time period
* Params:
  startTime: start of time period
  endTime: end of time period
  teamId: team slack id
  channelId (optional): include if you want to filter on channel
 */
const getPopularPosts = async (
  startTime,
  endTime,
  teamId,
  channelId,
  excludeChannels
) => {
  const supaClient = getSupaClient();
  const endDate = dayjs(endTime).add(1, "day").format(dateFormat);
  const base = supaClient
    .from("parent_msgs")
    .select(
      `reply_count, 
      reply_users,
      reaction_count,
      activity_score,
      text, 
      slack_user(image_32, name, is_admin),
      channel(name),
      permalink,
      formatted_ts`
    )
    .gte("formatted_ts", startTime)
    .lte("formatted_ts", endDate);

  if (!channelId) {
    base.filter("team", "eq", teamId);
  }

  if (channelId) {
    base.filter("channel", "eq", channelId);
  }
  if (excludeChannels) {
    excludeChannels.forEach((item) => {
      base.filter("channel", "neq", item);
    });
  }
  base.order("activity_score", { ascending: false }).limit(50);
  const { data, error } = await base;
  if (error) console.log("error in loadPopularPosts", error);
  return data;
};
export { getPopularPosts };

/** 
* Purpose: Get time to first reply (ttfr) for a given team and time period
* Params:
  startTime: start of time period
  endTime: end of time period
  teamId: team slack id
  channelId: channel slack id
 */
const getTTFRChannel = async (startTime, endTime, channelId) => {
  const supaClient = getSupaClient();

  const params = {
    start_time: startTime,
    end_time: endTime,
    channel_id: channelId,
  };
  const { data, error } = await supaClient.rpc("average_ttfr", params);
  if (error) console.error("error in getTTFRChannel", error);

  const average = await supaClient.rpc("average_ttfr_avg", params);
  if (average.error)
    console.error("error in getTTFRChannel (average)", average.error);

  return {
    data,
    average: average.data,
  };
};
export { getTTFRChannel };

/** 
* Purpose: Get the distribution of message types between 
* Params:
  startTime: start of time period
  endTime: end of time period
  teamId: team slack id
  channelId: (optional) include if you want to filter on channel slack id
 */
const getMessageType = async (startTime, endTime, teamId, channelId) => {
  const supaClient = getSupaClient();

  const params = {
    start_time: startTime,
    end_time: endTime,
  };
  let results = [];
  if (channelId) {
    params.channel_id = channelId;
    const { data, error } = await supaClient.rpc("message_type", params);
    if (error) console.error("error in getMessageType channelId", error);
    results = data;
  } else {
    params.team_id = teamId;
    const { data, error } = await supaClient.rpc(
      "message_type_overall",
      params
    );
    if (error) console.error("error in getMessageType overall", error);
    results = data;
  }
  return results;
};
export { getMessageType };

/** 
* Purpose: Get power users for a given team (and channel) over a time period
* Params:
  startTime: start of time period
  endTime: end of time period
  teamId: team slack id
  channelId: (optional) include if you want to filter on channel slack id
 */
const getPowerUsers = async (startTime, endTime, teamId, channelId) => {
  const supaClient = getSupaClient();
  const params = {
    start_time: startTime,
    end_time: endTime,
  };
  let results = [];
  if (channelId) {
    params.channel_id = channelId;
    const { data, error } = await supaClient.rpc("power_users_channel", params);
    if (error) console.error("error in getPowerUsers channelId", error);
    results = data;
  } else {
    params.team_id = teamId;
    const { data, error } = await supaClient.rpc("power_users_overall", params);
    if (error) console.error("error in getPowerUsers overall", error);
    results = data;
  }
  return results;
};
export { getPowerUsers };

/** 
* Purpose: Get average replies per post for a given channel over a time period
* Params:
  startTime: start of time period
  endTime: end of time period
  teamId: team slack id
  channelId: include if you want to filter on channel slack id
 */
const getAverageRepliesChannel = async (
  startTime,
  endTime,
  teamId,
  channelId
) => {
  const supaClient = getSupaClient();
  const params = {
    start_time: startTime,
    end_time: endTime,
    channel_id: channelId,
  };
  const { data, error } = await supaClient.rpc("average_replies", params);
  if (error) console.error("error in getAverageRepliesChannel", error);
  const average = await supaClient.rpc("average_replies_avg", params);
  if (average.error)
    console.error("error in getAverageRepliesChannel avg", average.error);
  return {
    data,
    average: average.data,
  };
};
export { getAverageRepliesChannel };

/** 
* Purpose: Get moderation tasks that need to be reviewed 
* Params:
  teamId: team slack id

 */
const getUserModerationHistory = async (slackId) => {
  const supaClient = getSupaClient();
  const params = {
    slack_user: slackId,
  };
  let results = [];
  const { data, error } = await supaClient
    .from("spam_msgs")
    .select(
      "text, reported_message_id, reported_by (name), slack_user, channel, channel_name, reported_text, direct_message, type, ts, formatted_ts, status"
    )
    .match(params)
    .order("ts", { ascending: false });
  window.analytics.track("User moderation history");
  if (data) {
    results = data.map((result) => {
      const timeSincePosted = timeSince(result.ts);
      let channelName = "";
      if (result.direct_message) {
        channelName = "DM";
      } else {
        channelName = `#${result.channel_name}`;
      }

      return {
        ...result,
        channelName,
        ageText: timeSincePosted.text,
        ageSeconds: timeSincePosted.seconds,
      };
    });
  }
  if (error) {
    return error;
  }
  return results;
};
export { getUserModerationHistory };

/** 
* Purpose: Get moderation tasks that need to be reviewed 
* Params:
  startTime: start of time period
  endTime: end of time period
  teamId: team slack id

 */
const getModerationQueue = async (params, toReview) => {
  const supaClient = getSupaClient();
  let results = [];
  let resultData = [];
  resultData = await supaClient
    .from("spam_msgs")
    .select(
      "text, key:reported_message_id, permalink, reported_text, reported_message_id, reported_by (name), slack_user(name,slack_id, image_32), channel, channel_name, direct_message, type, ts, formatted_ts, status"
    )
    .match(params)
    .order("ts", { ascending: false });

  window.analytics.track("User moderation history");
  if (resultData.data) {
    results = resultData.data.map((result) => {
      const timeSincePosted = timeSince(result.ts);
      let channelName = "";
      if (result.direct_message) {
        channelName = "DM";
      } else {
        channelName = `#${result.channel_name}`;
      }

      return {
        ...result,
        channelName,
        ageText: timeSincePosted.text,
        ageSeconds: timeSincePosted.seconds,
      };
    });
  }
  if (resultData.error) {
    return resultData.error;
  }
  return results;
};
export { getModerationQueue };

// load Team experience list to display in member directory edit
export const loadTeamExperience = async () => {
  const supaClient = getSupaClient();
  const teamId = localStorage.getItem("team-id");
  const teamExperience = await supaClient
    .from("directory_industry_experience")
    .select("name, id")
    .match({ team: teamId });
  if (teamExperience.error) {
    message.error("Error loading");
  }
  if (teamExperience.data) {
    return teamExperience.data;
  }
  return [];
};

// load Team skill list to display in member directory edit
export const loadTeamSkills = async () => {
  const supaClient = getSupaClient();
  const teamId = localStorage.getItem("team-id");
  const teamSkills = await supaClient
    .from("directory_skills")
    .select("name, id")
    .match({ team: teamId });
  if (teamSkills.error) {
    message.error("Error loading");
  }
  if (teamSkills.data) {
    return teamSkills.data;
  }
  return [];
};

/**
 * Get svix app id and integeky for a given workspace on local storage
 *
 * @returns object null or {appId, integKey} - null if a workspace wasn't found, or app id if found
 */
const loadWebhookKey = async () => {
  const supaClient = getSupaClient();
  const workspaceId =
    JSON.parse(localStorage.getItem("supabase.auth.token") || "{}")
      ?.currentSession.user?.app_metadata.workspace || "";
  if (workspaceId) {
    const { data, error } = await supaClient
      .from("svix_integrations")
      .select("app_id, key")
      .eq("workspace", workspaceId);
    if (data[0]) {
      const webhookAppId = data[0].app_id;
      localStorage.setItem("svix-app-id", webhookAppId);
      return { appId: webhookAppId, integKey: data[0].key };
    }
    if (error) {
      console.log("Error in loadWebhookKey", error);
    }
  }
  return { appId: null, integKey: null };
};
export { loadWebhookKey };

/**
 * Post to svix for the current workspace
 * @param {string} eventType  type of event that is sent. This is needed to make sure the links get the right events
 * they are subscribed to. Events need to be added to svix as well
 * @param {string} eventId id to be passed. If there isn't a unique id, pass in timestampe
 * @param {object} payload - object to send
 * tracks in segment if it fails. no return explicit
 */
const postSvixWebhook = async (eventType, eventId, payload) => {
  let svixAppId = localStorage.getItem("svix-app-id");
  if (!svixAppId) {
    svixAppId = await loadWebhookKey();
    if (!svixAppId) {
      window.analytics.track("Error sending webhook no app id", {
        eventType,
        eventId,
        payload,
      });
    }
  }
  try {
    const eventIdStr = String(eventId);
    // send a message to svix with event type so svix can send over the payload to subscribed URLs
    await svix.message.create(svixAppId, {
      eventType,
      eventId: eventIdStr,
      payload,
    });
  } catch (error) {
    console.log("Error", error.body);
    // only track if its not a retry message for Svix
    if (error.body.code !== "msg_exists") {
      window.analytics.track(`Error sending webhook`, {
        eventType,
        eventId,
        payload,
      });
    }
  }
};
export { postSvixWebhook };
